import React, { useEffect, useState } from "react";
import { Plus, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Skeleton } from "../../components/common";
import { toast } from "sonner";

export default function WhitelistPage() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);

  const isOwner = user?.role === "owner";

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/admins"); setAdmins(data.admins); }
    catch { toast.error("Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admins", { email });
      toast.success("Admin added — invite sent");
      setLastInvite({ email, token: data.stub_invite_token });
      setEmail("");
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try { await api.delete(`/admins/${id}`); toast.success("Admin removed"); load(); }
    catch (ex) { toast.error(ex?.response?.data?.detail || "Failed"); }
  };

  return (
    <div data-testid="admin-whitelist-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-semibold tracking-tight">Admin Whitelist</h1>
        <p className="text-sm text-[var(--text-secondary)] mt-1">{admins.length} admin{admins.length === 1 ? "" : "s"}</p>
      </div>

      {/* Branding has moved to Settings */}

      <div className="wl-card-solid p-6 mb-6">
        <div className="flex items-center gap-2 mb-1.5">
          <ShieldCheck size={16} className="text-[var(--brand)]" />
          <h2 className="font-display text-base font-semibold">Allowed admin emails</h2>
        </div>
        <p className="text-sm text-[var(--text-secondary)] mb-5">Only @wishlink.com emails on this list can access this admin panel.</p>
        <form onSubmit={submit} className="flex gap-2">
          <input className="wl-input flex-1" type="email" placeholder="someone@wishlink.com" required
            value={email} onChange={(e) => setEmail(e.target.value)} disabled={!isOwner || busy} data-testid="whitelist-email" />
          <button className="wl-btn-primary inline-flex items-center gap-2" disabled={!isOwner || busy} data-testid="whitelist-add">
            <Plus size={16} /> Add email
          </button>
        </form>
        {!isOwner && <p className="text-xs text-[var(--text-secondary)] mt-3">Only the Owner can add or remove admins.</p>}
        {lastInvite && (
          <div className="mt-4 wl-card p-3 text-xs">
            <div className="font-semibold mb-1">Prototype mode — invite link for {lastInvite.email}:</div>
            <a href={`/set-password/${lastInvite.token}`} className="text-[var(--brand)] break-all" data-testid="whitelist-stub-link">/set-password/{lastInvite.token}</a>
          </div>
        )}
      </div>

      <div className="wl-card-solid overflow-hidden">
        {loading ? <div className="p-6 space-y-2">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}</div>
        : <table className="wl-table">
          <thead><tr><th>Email</th><th>Role</th><th>Status</th><th>Date added</th><th className="text-right pr-6">Actions</th></tr></thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.id} data-testid={`admin-row-${a.id}`}>
                <td className="font-medium">{a.email}</td>
                <td>{a.role === "owner" ? <span className="wl-pill" style={{ background: "var(--brand-tint)", color: "var(--brand)" }}>Owner</span> : <span className="wl-pill wl-pill-neutral">Admin</span>}</td>
                <td><span className={`wl-pill ${a.status === "active" ? "wl-pill-active" : "wl-pill-pending"}`}>{a.status || "active"}</span></td>
                <td className="text-[var(--text-secondary)]">{new Date(a.created_at).toLocaleDateString("en-IN")}</td>
                <td className="text-right pr-6">
                  {a.role !== "owner" && isOwner ? <button onClick={() => remove(a.id)} className="wl-btn-destructive" data-testid={`admin-remove-${a.id}`}>Remove</button> : <span className="text-xs text-[var(--text-secondary)]">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>}
      </div>
    </div>
  );
}
